import React, { useState } from 'react';
import { 
  CrownIcon, 
  ClockIcon, 
  ZapIcon, 
  FileTextIcon, 
  AlertCircleIcon,
  CheckCircleIcon,
  CopyIcon,
  CheckIcon,
  SearchIcon,
  ExpandIcon,
  XIcon
} from 'lucide-react';

// Code block parser and formatter (keeping existing code)
const parseCodeBlocks = (content) => {
  const patterns = [
    /```(\w+)?\n?([\s\S]*?)```/g,
    /`([^`\n]+)`/g,
  ];
  
  let parsedContent = [];
  let lastIndex = 0;
  
  const codeBlocks = [];
  patterns.forEach((pattern, patternIndex) => {
    let match;
    while ((match = pattern.exec(content)) !== null) {
      codeBlocks.push({
        start: match.index,
        end: match.index + match[0].length,
        language: patternIndex === 0 ? (match[1] || 'code') : 'inline',
        code: patternIndex === 0 ? match[2].trim() : match[1],
        fullMatch: match[0],
        type: patternIndex === 0 ? 'block' : 'inline'
      });
    }
  });
  
  codeBlocks.sort((a, b) => a.start - b.start);
  
  codeBlocks.forEach((block, index) => {
    if (block.start > lastIndex) {
      const textBefore = content.slice(lastIndex, block.start);
      if (textBefore.trim()) {
        parsedContent.push({
          type: 'text',
          content: textBefore
        });
      }
    }
    
    parsedContent.push(block);
    lastIndex = block.end;
  });
  
  if (lastIndex < content.length) {
    const remainingText = content.slice(lastIndex);
    if (remainingText.trim()) {
      parsedContent.push({
        type: 'text',
        content: remainingText
      });
    }
  }
  
  if (parsedContent.length === 0) {
    parsedContent.push({
      type: 'text',
      content: content
    });
  }
  
  return parsedContent;
};

// Code Block Component (keeping existing)
const CodeBlock = ({ language, code, type }) => {
  const [copied, setCopied] = useState(false);
  
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code: ', err);
    }
  };

  if (type === 'inline') {
    return (
      <code className="bg-cyan-500/10 text-cyan-300 px-2 py-1 rounded-md text-sm font-code border border-cyan-500/20">
        {code}
      </code>
    );
  }

  return (
    <div className="glass border border-slate-600/30 rounded-xl my-4 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 bg-slate-800/70 border-b border-slate-600/30">
        <span className="text-cyan-300 text-sm font-bold capitalize flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-cyan-500/30 border border-cyan-500/50"></div>
          {language === 'code' ? 'Code' : language}
        </span>
        <button
          onClick={copyCode}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            copied
              ? 'text-green-300 bg-green-500/20 border border-green-500/30'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50 border border-transparent'
          }`}
        >
          {copied ? (
            <>
              <CheckIcon className="h-3 w-3" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <CopyIcon className="h-3 w-3" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      <div className="p-5 bg-slate-900/80">
        <pre className="text-slate-200 text-sm font-code overflow-x-auto whitespace-pre-wrap leading-relaxed">
          {code}
        </pre>
      </div>
    </div>
  );
};

// Enhanced Response Content Component (keeping existing)
const ResponseContent = ({ content }) => {
  const parsedContent = parseCodeBlocks(content);
  
  return (
    <div>
      {parsedContent.map((item, index) => {
        if (item.type === 'text') {
          return (
            <div key={index} className="whitespace-pre-wrap text-slate-200 font-sans leading-relaxed">
              {item.content}
            </div>
          );
        } else if (item.type === 'block' || item.type === 'inline') {
          return (
            <CodeBlock
              key={index}
              language={item.language}
              code={item.code}
              type={item.type}
            />
          );
        }
        return null;
      })}
    </div>
  );
};

// NEW: Expand Modal Component
const ExpandModal = ({ isOpen, onClose, title, subtitle, content, color }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const copyFullContent = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const colorClasses = {
    emerald: 'border-emerald-500/50 bg-gradient-to-br from-emerald-500/5 to-emerald-600/5',
    blue: 'border-blue-500/50 bg-gradient-to-br from-blue-500/5 to-blue-600/5',
    purple: 'border-purple-500/50 bg-gradient-to-br from-purple-500/5 to-purple-600/5'
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`bg-slate-800 border rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl ${
        colorClasses[color] || 'border-slate-700'
      }`}>
        
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
            <p className="text-slate-400 text-sm mt-1">{subtitle}</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={copyFullContent}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                copied 
                  ? 'text-green-400 bg-green-400/10' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
              title={copied ? 'Copied!' : 'Copy full response'}
            >
              {copied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
              <span>{copied ? 'Copied!' : 'Copy'}</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
              title="Close"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="prose prose-invert prose-lg max-w-none">
            <ResponseContent content={content} />
          </div>
        </div>
      </div>
    </div>
  );
};

const ModelBox = ({ title, subtitle, result, isWinner, score, color, onAnalyze, canAnalyze = false, isDisqualified = false }) => {
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // NEW: Modal state

  const safeResult = result || { success:false, content:'No response', responseTime:0, tokens:0, model:title };

  const isError = !isDisqualified && safeResult && !safeResult.success;
  const hasSuccess =
    !!result && (result.success !== false) && !result.error && (typeof result.content === 'string' ? true : true);

  const enabled = Boolean(onAnalyze) && hasSuccess && !isError && !isDisqualified;

  const colorClasses = {
    emerald: {
      border: 'border-emerald-500/50',
      bg: 'from-emerald-500/10 to-emerald-600/5',
      text: 'text-emerald-400',
      crown: 'text-emerald-400'
    },
    blue: {
      border: 'border-blue-500/50',
      bg: 'from-blue-500/10 to-blue-600/5',
      text: 'text-blue-400',
      crown: 'text-blue-400'
    },
    purple: {
      border: 'border-purple-500/50',
      bg: 'from-purple-500/10 to-purple-600/5',
      text: 'text-purple-400',
      crown: 'text-purple-400'
    }
  };

  const colors = colorClasses[color] || colorClasses.blue;

  const copyToClipboard = async () => {
    if (!safeResult.content || !safeResult.success) return;
    
    try {
      await navigator.clipboard.writeText(safeResult.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <>
      <div className={`relative glass border rounded-2xl p-6 transition-all duration-300 ${
        isWinner
          ? `${colors.border} bg-gradient-to-br ${colors.bg} winner-glow shadow-2xl`
          : 'border-slate-700/50 hover:border-slate-600 card-hover'
      }`}>

        {/* Winner Crown - Enhanced */}
        {isWinner && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
            <div className={`glass p-3 rounded-full border-2 ${colors.border} shadow-lg`}>
              <CrownIcon className={`h-6 w-6 ${colors.crown} animate-pulse`} />
            </div>
          </div>
        )}

        {/* Header - Enhanced */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className={`font-black text-xl ${isWinner ? colors.text : 'text-white'}`}>
              {title}
            </h3>
            <div className="flex items-center gap-2">
              {/* Expand button - Enhanced */}
              {!isError && !isDisqualified && safeResult.content && (
                <button
                  onClick={() => setIsExpanded(true)}
                  className="p-2 rounded-lg glass border border-slate-600/30 text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-all"
                  title="Expand response"
                >
                  <ExpandIcon className="h-4 w-4" />
                </button>
              )}

              {/* Analyze button - Enhanced */}
              {enabled && (
                <button
                  type="button"
                  onClick={() => {
                    if (!enabled) return;
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    onAnalyze();
                  }}
                  className="p-2 rounded-lg glass border border-slate-600/30 text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-all"
                  title="Analyze this answer"
                >
                  <SearchIcon className="h-4 w-4" />
                </button>
              )}

              {!enabled && (
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  className="p-2 rounded-lg border border-slate-700/30 text-slate-600 opacity-50 cursor-not-allowed pointer-events-none"
                  title={
                    isDisqualified
                      ? 'This model is disqualified'
                      : hasSuccess
                      ? 'No opponents left to analyze'
                      : 'No successful response to analyze'
                  }
                  onClick={(e) => e.preventDefault()}
                >
                  <SearchIcon className="h-4 w-4" />
                </button>
              )}

              {!isError && !isDisqualified && (
                <button
                  onClick={copyToClipboard}
                  className={`p-2 rounded-lg glass border transition-all ${
                    copied
                      ? 'text-green-400 bg-green-400/20 border-green-500/50'
                      : 'text-slate-400 hover:text-white hover:border-slate-500/50 border-slate-600/30'
                  }`}
                  title={copied ? 'Copied!' : 'Copy response'}
                >
                  {copied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
                </button>
              )}

              {/* Status Icon */}
              {isDisqualified ? (
                <div className="h-6 w-6 flex items-center justify-center text-orange-400 font-black text-xl">
                  ×
                </div>
              ) : isError ? (
                <AlertCircleIcon className="h-6 w-6 text-red-400" />
              ) : (
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              )}
            </div>
          </div>
          <p className="text-slate-400 text-sm font-medium">{subtitle}</p>
        </div>

        {/* Response Content - Enhanced */}
        <div className="mb-5">
          <div className="bg-slate-900/70 rounded-xl p-5 min-h-[200px] max-h-[450px] overflow-y-auto border border-slate-700/30 scrollbar-thin">
            {isDisqualified ? (
              <div className="text-center py-16">
                <div className="relative">
                  <div className="text-8xl font-black text-slate-700/30 mb-3">×</div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="glass px-6 py-3 rounded-xl border border-orange-500/30">
                      <p className="text-orange-300 font-black text-xl">DISQUALIFIED</p>
                    </div>
                  </div>
                </div>
                <p className="text-slate-400 text-sm mt-6 font-medium">
                  Being analyzed by other models
                </p>
              </div>
            ) : isError ? (
              <div className="text-center py-12">
                <AlertCircleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
                <p className="text-red-300 font-bold text-lg mb-2">Response Failed</p>
                <p className="text-slate-400 text-sm">{safeResult.content}</p>
              </div>
            ) : (
              <ResponseContent content={safeResult.content} />
            )}
          </div>
        </div>

        {/* Stats - Enhanced */}
        <div className="grid grid-cols-2 gap-3 text-xs mb-5">
          <div className="glass rounded-xl p-4 border border-slate-700/30">
            <div className="flex items-center gap-2 mb-2">
              <ClockIcon className="h-4 w-4 text-cyan-400" />
              <span className="text-slate-300 font-semibold">Response Time</span>
            </div>
            <p className="text-white font-bold text-lg">
              {isDisqualified ? '—' : (safeResult.responseTime / 1000).toFixed(2) + 's'}
            </p>
          </div>

          <div className="glass rounded-xl p-4 border border-slate-700/30">
            <div className="flex items-center gap-2 mb-2">
              <ZapIcon className="h-4 w-4 text-yellow-400" />
              <span className="text-slate-300 font-semibold">Tokens</span>
            </div>
            <p className="text-white font-bold text-lg">
              {isDisqualified ? '—' : safeResult.tokens.toLocaleString()}
            </p>
          </div>

          <div className="glass rounded-xl p-4 border border-slate-700/30">
            <div className="flex items-center gap-2 mb-2">
              <FileTextIcon className="h-4 w-4 text-purple-400" />
              <span className="text-slate-300 font-semibold">Words</span>
            </div>
            <p className="text-white font-bold text-lg">
              {isDisqualified ? '—' : (isError ? 0 : safeResult.content.trim().split(/\s+/).length)}
            </p>
          </div>

          <div className="glass rounded-xl p-4 border border-slate-700/30">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-slate-300 font-semibold">Score</span>
            </div>
            <p className={`font-black text-2xl ${isDisqualified ? 'text-orange-400' : (isError ? 'text-red-400' :
              isWinner ? colors.text : 'text-white')}`}>
              {isDisqualified ? 'DQ' : Math.round(score?.total || 0) + '/100'}
            </p>
          </div>
        </div>

        {/* Score Breakdown - Enhanced */}
        {score && !isError && !isDisqualified && (
          <div className="pt-5 border-t border-slate-700/50">
            <p className="text-slate-300 text-sm font-semibold mb-3">Score Breakdown</p>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="flex justify-between items-center px-3 py-2 bg-slate-900/40 rounded-lg">
                <span className="text-slate-400 font-medium">Success</span>
                <span className="text-white font-bold">{Math.round(score.breakdown?.success || 0)}</span>
              </div>
              <div className="flex justify-between items-center px-3 py-2 bg-slate-900/40 rounded-lg">
                <span className="text-slate-400 font-medium">Speed</span>
                <span className="text-white font-bold">{Math.round(score.breakdown?.speed || 0)}</span>
              </div>
              <div className="flex justify-between items-center px-3 py-2 bg-slate-900/40 rounded-lg">
                <span className="text-slate-400 font-medium">Complete</span>
                <span className="text-white font-bold">{Math.round(score.breakdown?.completeness || 0)}</span>
              </div>
              <div className="flex justify-between items-center px-3 py-2 bg-slate-900/40 rounded-lg">
                <span className="text-slate-400 font-medium">Accuracy</span>
                <span className="text-white font-bold">{Math.round(score.breakdown?.accuracy || 0)}</span>
              </div>
              <div className="flex justify-between items-center px-3 py-2 bg-slate-900/40 rounded-lg">
                <span className="text-slate-400 font-medium">Structure</span>
                <span className="text-white font-bold">{Math.round(score.breakdown?.structure || 0)}</span>
              </div>
              <div className="flex justify-between items-center px-3 py-2 bg-slate-900/40 rounded-lg">
                <span className="text-slate-400 font-medium">Depth</span>
                <span className="text-white font-bold">{Math.round(score.breakdown?.depth || 0)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* NEW: Expand Modal */}
      <ExpandModal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        title={title}
        subtitle={subtitle}
        content={safeResult.content}
        color={color}
      />
    </>
  );
};

export default ModelBox;